import {
  Button,
  Input,
  List,
  Modal,
  Table,
  TableColumnsType,
  Tabs,
  InputNumber,
  Tag,
} from "antd";
import { Search, X, Plus, Minus, Package } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { useGetAllSupplies } from "~/services/operation";
import { useBooking } from "~/store";
import { TSupply, TServiceTicketSupplyItem } from "~/types";
import { SuppliesType } from "~/utils/constants";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useGetSupplyKits, SupplyKit } from "~/services/supply-kit";

type Props = {
  value?: Omit<TServiceTicketSupplyItem, "id">[];
  onChange?: (value: Omit<TServiceTicketSupplyItem, "id">[]) => void;
  viewOnly?: boolean; // Optional prop to override viewMode
  skipStockCheck?: boolean; // For estimates - don't enforce stock limits
};

export const Supplies: FC<Props> = ({ value = [], onChange, viewOnly: viewOnlyProp, skipStockCheck = false }) => {
  const { viewMode } = useBooking();

  const viewOnly = useMemo(() => viewOnlyProp ?? viewMode === "view", [viewOnlyProp, viewMode]);

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [search] = useDebounceValue(searchTerm, 500);
  const [activeTab, setActiveTab] = useState<string>("all");
  // Add state to track quantities for each supply
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const { data: supplies, isLoading } = useGetAllSupplies({
    page,
    pageSize: 10,
    search,
    suppliesType: activeTab !== "all" && activeTab !== "kits" ? (activeTab as SuppliesType) : undefined,
  });

  const { data: kits, isLoading: isLoadingKits } = useGetSupplyKits({
    page,
    pageSize: 10,
    search,
  });

  const [showSupplyModal, setShowSupplyModal] = useState(false);

  const handleAddKit = (kit: SupplyKit) => {
    // Calculate total billable amount of all items in the kit
    const totalBillable = kit.items.reduce((sum, item) => {
      // Only count billable items in the total
      if (!item.supply?.isBillable) {
        return sum;
      }

      // Use billableRate if set, otherwise use price
      const itemPrice = item.supply.billableRate
        ? Number(item.supply.billableRate)
        : Number(item.supply.price || 0);
      const itemTotal = itemPrice * item.quantity;
      return sum + itemTotal;
    }, 0);

    // Check if this kit is already added
    const existingIndex = value.findIndex((item) => item.kitId === kit.id);

    if (existingIndex >= 0) {
      // Increase quantity of existing kit
      const updatedSupplies = [...value];
      updatedSupplies[existingIndex].quantity += 1;
      onChange?.(updatedSupplies);
    } else {
      // Add kit as a single item
      const newKitItem: Omit<TServiceTicketSupplyItem, "id"> = {
        kitId: kit.id,
        isKit: true,
        quantity: 1,
        unitCost: totalBillable,
        kit: kit,
      };
      onChange?.([...value, newKitItem]);
    }
  };

  const handleRemoveSupply = (supplyId?: string, kitId?: string) => {
    const updatedSupplies = value.filter((item) => {
      if (kitId) return item.kitId !== kitId;
      return item.supplyId !== supplyId;
    });
    onChange?.(updatedSupplies);
  };

  const handleQuantityChange = (supplyId: string, quantity: number) => {
    if (quantity <= 0) return;
    const updatedSupplies = value.map((item) =>
      item.supplyId === supplyId ? { ...item, quantity } : item
    );
    onChange?.(updatedSupplies);
  };

  const handleAddSupply = (supply: TSupply, quantity: number = 1) => {
    const existingIndex = value.findIndex(
      (item) => item.supplyId === supply.id
    );

    if (existingIndex >= 0) {
      // Update existing quantity
      const updatedSupplies = [...value];
      updatedSupplies[existingIndex] = {
        ...updatedSupplies[existingIndex],
        quantity: updatedSupplies[existingIndex].quantity + quantity,
      };
      onChange?.(updatedSupplies);
    } else {
      // Add new supply item - use billableRate for customer pricing, fallback to price then cost
      const newSupplyItem: Omit<TServiceTicketSupplyItem, "id"> = {
        supplyId: supply.id,
        quantity,
        unitCost: Number(supply.billableRate) || Number(supply.price) || Number(supply.cost) || 0,
        supply,
      };
      onChange?.([...value, newSupplyItem]);
    }
  };

  // Handle quantity input change
  const handleQuantityInputChange = (supplyId: string, quantity: number) => {
    setQuantities((prev) => ({
      ...prev,
      [supplyId]: quantity,
    }));
  };

  const columns: TableColumnsType<TSupply> = [
    {
      title: "ID",
      dataIndex: "supplyRef",
      key: "ref",
      width: "10%",
      render: (id) => (
        <div className="text-gray-700 font-mono text-xs">{id}</div>
      ),
    },
    {
      title: "Supply",
      dataIndex: "name",
      key: "name",
      width: "20%",
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.name}</div>
          <div className="text-xs text-gray-500">
            {record.brand && `${record.brand} · `}
            {record.category && `${record.category}`}
          </div>
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: ["location", "name"],
      key: "location",
      width: "20%",
    },
    {
      title: "Stock",
      dataIndex: "currentStock",
      key: "stock",
      width: "20%",
      render: (stock, record) => (
        <div className="flex items-center">
          <span>
            {stock} {record.unit}
          </span>
          {stock <= record.minimumStock && (
            <span className="ml-1 text-red-500">(Low)</span>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "",
      key: "quantity",
      align: "center",
      width: "20%",
      render: (_, supply) => {
        const existingItem = value.find((item) => item.supplyId === supply.id);
        const currentQuantity = existingItem?.quantity || 1;
        const maxStock = supply.currentStock || 0;

        if (existingItem) {
          return (
            <div className="flex items-center justify-center gap-2 w-full">
              <Button
                size="small"
                onClick={() =>
                  handleQuantityChange(
                    supply.id,
                    Math.max(1, currentQuantity - 1)
                  )
                }
                disabled={viewOnly || currentQuantity <= 1}
                icon={<Minus size={12} />}
              />
              <InputNumber
                size="small"
                min={1}
                max={skipStockCheck ? undefined : maxStock}
                value={currentQuantity}
                onChange={(value) =>
                  handleQuantityChange(supply.id, value || 1)
                }
                disabled={viewOnly}
                className="w-16"
              />
              <Button
                size="small"
                onClick={() =>
                  handleQuantityChange(
                    supply.id,
                    skipStockCheck ? currentQuantity + 1 : Math.min(maxStock, currentQuantity + 1)
                  )
                }
                disabled={viewOnly || (!skipStockCheck && currentQuantity >= maxStock)}
                icon={<Plus size={12} />}
              />
            </div>
          );
        }

        return (
          <InputNumber
            size="small"
            min={1}
            max={skipStockCheck ? undefined : maxStock}
            defaultValue={1}
            value={quantities[supply.id] || 1}
            onChange={(value) =>
              handleQuantityInputChange(supply.id, value || 1)
            }
            placeholder="Qty"
            disabled={viewOnly || (!skipStockCheck && maxStock <= 0)}
            className="w-16"
          />
        );
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "x",
      align: "center",
      width: "15%",
      render: (_, supply) => {
        const existingItem = value.find((item) => item.supplyId === supply.id);
        // Use the quantity from state instead of DOM query
        const quantity = quantities[supply.id] || 1;

        return (
          <Button
            disabled={(!skipStockCheck && (supply.currentStock ?? 0) <= 0) || viewOnly}
            size="small"
            htmlType="button"
            onClick={() => {
              if (existingItem) {
                handleRemoveSupply(supply.id);
              } else {
                handleAddSupply(supply, quantity);
              }
            }}
            type={existingItem ? "default" : "primary"}
            className={`px-3 py-1 border rounded-2xl`}
          >
            {existingItem ? "Remove" : "Add"}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <label className="block text-sm font-medium text-gray-700">
          Supplies
        </label>
        <Button
          size="small"
          type="link"
          onClick={() => setShowSupplyModal(true)}
          className="text-sm text-blue-600 hover:text-blue-800 p-0"
          disabled={viewOnly}
        >
          Add Supplies
        </Button>
      </div>
      <div className="border rounded-lg p-2 min-h-[100px]">
        {(value?.length ?? 0) > 0 ? (
          <List
            size="small"
            dataSource={value}
            renderItem={(item) => {
              // Check if this is a kit
              if (item.isKit && item.kit) {
                const kit = item.kit;
                const itemsList = kit.items
                  .map((kitItem) => `${kitItem.supply?.name} (${kitItem.quantity})`)
                  .join(", ");

                return (
                  <List.Item className="flex justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Tag color="blue" icon={<Package size={12} />}>
                          {kit.kitRef}
                        </Tag>
                        <span className="font-medium">{kit.name}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-sm font-medium text-blue-600">
                          Qty: {item.quantity}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-600">
                          Contains: {itemsList}
                        </span>
                      </div>
                      {item.unitCost && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm font-medium">
                            ${(item.unitCost * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                    {!viewOnly && (
                      <X
                        size={14}
                        className="ml-1 cursor-pointer text-red-500"
                        onClick={() => handleRemoveSupply(undefined, kit.id)}
                      />
                    )}
                  </List.Item>
                );
              }

              // Regular supply item
              const supply =
                item.supply ||
                supplies?.items.find((supply) => supply.id === item.supplyId);

              if (!supply) return null;
              return (
                <List.Item className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">
                        {supply.supplyRef}
                      </span>
                      <span className="font-medium">{supply.name}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-sm">{supply.location?.name}</span>
                      <span className="text-gray-500">•</span>
                      <span className="text-sm font-medium text-blue-600">
                        Qty: {item.quantity}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-gray-500">•</span>
                      <span
                        className={`text-sm ${
                          (supply.currentStock ?? 0) <= supply.minimumStock
                            ? "text-red-500"
                            : ""
                        }`}
                      >
                        Available: {supply.currentStock} {supply.unit}
                      </span>
                      {item.unitCost && (
                        <>
                          <span className="text-gray-500">•</span>
                          <span className="text-sm">
                            ${(item.unitCost * item.quantity).toFixed(2)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {!viewOnly && (
                    <X
                      size={14}
                      className="ml-1 cursor-pointer text-red-500"
                      onClick={() => handleRemoveSupply(supply.id)}
                    />
                  )}
                </List.Item>
              );
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-16 text-gray-400">
            No supplies added
          </div>
        )}
      </div>

      {/* Supply Selection Modal */}
      <Modal
        title="Select Supplies"
        open={showSupplyModal}
        onCancel={() => setShowSupplyModal(false)}
        footer={null}
        width={"60%"}
        getContainer={() => document.body}
        zIndex={2000}
      >
        <div className="mb-4">
          <Input
            prefix={<Search className="mr-1 text-gray-400" />}
            placeholder="Search supplies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setPage(1); // Reset page when switching tabs
          }}
          items={[
            { key: "all", label: "All Supplies" },
            { key: "kits", label: "Supply Kits" },
            {
              key: SuppliesType.TOOLS_KITS_AND_HARDWARE,
              label: "Tools, Kits & Hardware",
            },
            {
              key: SuppliesType.INTERNAL_SUPPLIES_AND_FACILITIES,
              label: "Internal Supplies & Facilities",
            },
            {
              key: SuppliesType.PACKAGING_MATERIALS,
              label: "Raw Materials",
            },
            {
              key: SuppliesType.REFURBISHED_CRATES,
              label: "Refurbished Crates",
            },
            { key: SuppliesType.PPE, label: "PPE" },
            { key: SuppliesType.EQUIPMENT, label: "Equipment" },
          ]}
        />

        {activeTab === "kits" ? (
          <Table
            columns={[
              {
                title: "Kit Reference",
                dataIndex: "kitRef",
                key: "kitRef",
                render: (ref) => <Tag color="blue">{ref}</Tag>,
              },
              {
                title: "Name",
                dataIndex: "name",
                key: "name",
                render: (name) => <span className="font-medium">{name}</span>,
              },
              {
                title: "Items",
                dataIndex: "items",
                key: "items",
                render: (items: SupplyKit["items"]) => (
                  <div className="flex flex-wrap gap-1">
                    {items.slice(0, 3).map((item) => (
                      <Tag key={item.id} icon={<Package size={12} />}>
                        {item.supply?.name} (×{item.quantity})
                      </Tag>
                    ))}
                    {items.length > 3 && (
                      <Tag>+{items.length - 3} more</Tag>
                    )}
                  </div>
                ),
              },
              {
                title: "",
                key: "action",
                align: "center",
                render: (_, kit: SupplyKit) => (
                  <Button
                    size="small"
                    type="primary"
                    disabled={viewOnly}
                    onClick={() => handleAddKit(kit)}
                  >
                    Add Kit
                  </Button>
                ),
              },
            ]}
            dataSource={kits?.items ?? []}
            rowKey="id"
            loading={isLoadingKits}
            pagination={{
              current: page,
              pageSize: 10,
              total: kits?.meta?.totalItems ?? 0,
              onChange: setPage,
              showSizeChanger: false,
            }}
            size="small"
          />
        ) : (
          <Table
            columns={columns}
            dataSource={supplies?.items ?? []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              current: page,
              pageSize: 10,
              total: supplies?.meta?.totalItems ?? 0,
              onChange: setPage,
              showSizeChanger: false,
            }}
            size="small"
          />
        )}

        <div className="mt-4 flex justify-end">
          <Button onClick={() => setShowSupplyModal(false)}>Done</Button>
        </div>
      </Modal>
    </>
  );
};
